import type { ModalProps } from "@hopper-ui/components";
import { Button, Content, Heading, Modal, Stack, Text } from "@hopper-ui/components";
import { crawlingStatusesQueryOptions } from "../api/routes/crawlers.ts";
import { initialCrawlerNames } from "../api/initialCrawl.ts";
import { useTrackModalState } from "@sg-protect/shell";
import { useQuery } from "@tanstack/react-query";
import { useLocalStorage } from "@uidotdev/usehooks";
import { useEffect, useMemo } from "react";

const MODAL_NAME = "Initial Crawl Modal";

export const InitialCrawlModalTrigger = () => {
    const { data: crawlingStatuses } = useQuery(crawlingStatusesQueryOptions());
    const [initialCrawlStorage, setInitialCrawlStorage] = useLocalStorage<{ dismissed: boolean }>(
        "modal:initial-crawl",
        { dismissed: false }
    );
    const [trackModalOpened, trackModalClosed] = useTrackModalState();

    const shouldDisplayModal = useMemo(() => {
        return (
            crawlingStatuses &&
            initialCrawlerNames.some(crawler => crawlingStatuses[crawler]?.crawlState === "initializing") &&
            !initialCrawlStorage?.dismissed
        );
    }, [crawlingStatuses, initialCrawlStorage?.dismissed]);

    useEffect(() => {
        if (shouldDisplayModal) {
            trackModalOpened(MODAL_NAME, "First Time Experience");
        }
    }, [shouldDisplayModal, trackModalOpened]);

    if (!shouldDisplayModal) {
        return null;
    }

    return (
        <InitialCrawlModal
            defaultOpen
            onOpenChange={isOpen => {
                if (!isOpen) {
                    trackModalClosed(MODAL_NAME, "Explore ShareGate Protect");
                    setInitialCrawlStorage({ dismissed: true });
                }
            }}
        />
    );
};

type InitialCrawlModalProps = ModalProps;

const InitialCrawlModal = ({ defaultOpen, onOpenChange, ...props }: InitialCrawlModalProps) => {
    return (
        <Modal
            defaultOpen={defaultOpen}
            onOpenChange={onOpenChange}
            isDismissable={false}
            size="sm"
            aria-label="Explore ShareGate Protect"
            data-public
            {...props}
        >
            {({ close }) => (
                <Content>
                    <Stack alignX="center" gap="stack-lg">
                        <Stack alignX="center">
                            <Heading>Your first scan is underway</Heading>
                        </Stack>
                        <Text size="sm" textAlign="center">
                            We're scanning every site, permission, and guest link in your tenant to surface governance
                            insights. This may take a while, but results will appear as they're ready.
                        </Text>

                        <Button isFluid onPress={close}>
                            Explore ShareGate Protect
                        </Button>
                    </Stack>
                </Content>
            )}
        </Modal>
    );
};
